import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import {
	DEEP_RESEARCH_SLOW_AFTER_MS,
	formatElapsed,
	isTakingLong,
	useNow,
} from "@/utils/generationTiming";

import type { DeepResearchRunView } from "@/pages/Private/pages/ProfileTracker/providers/deepResearchRunState";

/**
 * A research in flight (PREC-4052): a grey dot and "Queued · N min" while it waits for a slot,
 * the spinner and "Researching… · N min" once a worker has it, and a "taking longer than usual"
 * line past the running p95 (`DEEP_RESEARCH_SLOW_AFTER_MS`).
 */
export const ResearchRunStatus: FunctionComponent<{ run: DeepResearchRunView | null }> = ({
	run,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, string>) =>
		t(`profileTracker.deepResearch.${key}`, options);
	const now = useNow(!!run);
	const isQueued = run?.phase === "queued";
	const elapsed = formatElapsed(run ? now - run.since : 0);
	const takingLong = isTakingLong(run?.since ?? null, now, DEEP_RESEARCH_SLOW_AFTER_MS);

	return (
		<div
			className="flex flex-col items-center justify-center py-12"
			data-testid="research-run-status"
		>
			{isQueued ? (
				<span className="mb-6 h-3 w-3 animate-pulse rounded-full bg-text-tertiary" />
			) : (
				<svg
					className="mb-6 h-10 w-10 animate-spin text-blue-action"
					fill="none"
					viewBox="0 0 24 24"
				>
					<circle
						className="opacity-25"
						cx="12"
						cy="12"
						r="10"
						stroke="currentColor"
						strokeWidth="4"
					/>
					<path
						className="opacity-75"
						d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						fill="currentColor"
					/>
				</svg>
			)}
			<h3 className="mb-2 text-md font-semibold text-text-meta">
				{run ? ts(isQueued ? "queuedTitle" : "runningTitle", { elapsed }) : ts("inProgressTitle")}
			</h3>
			<p className="max-w-xs text-center text-sm text-text-meta/80">
				{ts(isQueued ? "queuedDescription" : "inProgressDescription")}
			</p>
			{takingLong && (
				<p
					className="mt-2 max-w-xs text-center text-xs text-text-meta"
					data-testid="research-taking-long"
				>
					{ts(isQueued ? "slowQueued" : "slowRunning")}
				</p>
			)}
		</div>
	);
};
